<script lang="ts" setup>
import { computed, onMounted, type PropType, ref, watch, toRaw } from 'vue';
import dateFormat from 'date-fns/format';
import type { VTextField } from 'vuetify/components';

const props = defineProps({
  modelValue: {
    type: [Object, String, Number, null] as PropType<Date | string | number | null>,
    default: undefined
  },
  format: {
    type: String,
    default: 'yyyy-MM'
  },
  appendInnerIcon: {
    type: String,
    default: 'mdi-calendar'
  },
  returnFormat: {
    type: String,
    default: 'yyyy-MM-dd'
  },
  returnFormatted: {
    type: Boolean,
    default: true
  },
  monthDay: {
    type: Number,
    default: 1
  },
  returnType: {
    type: String,
    default: 'string'
  },
  min: {
    type: String
  },
  max: {
    type: String
  }
});

const pickerYear = ref(new Date().getFullYear());

const emit = defineEmits(['update:modelValue', 'blur', 'input']);

const field = ref<VTextField | null>(null);
const open = ref(false);
const date = ref(props.modelValue ? [new Date(props.modelValue)] : []);
const dateText = ref('');
const renderKey = ref(0); // required for rerender after updating text value
const emptyResult = ref(props.modelValue === null ? null : undefined);
const isString = ref(typeof props.modelValue === 'string' || props.returnType === 'string');
const isNumber = ref(typeof props.modelValue === 'number' || props.returnType === 'number');

onMounted(() => {
  setDate(date.value);
});

const computedValue = computed(() => props.modelValue);

watch(computedValue, (value) => {
  if (value) {
    date.value = [new Date(toRaw(value))];
    setDate(date.value);
  } else {
    date.value = [];
    dateText.value = '';
  }
});

const setDate = (value: Date[]) => {
  if (!value[0]) return;

  date.value = value;
  dateText.value = formatText(value[0]);
};

const update = (value: readonly Date[]) => {
  const typeSafeValue = Array.isArray(value) ? value : [value];
  setDate(typeSafeValue);

  const result = typeSafeValue[0] ? getReturnValue(typeSafeValue[0]) : emptyResult.value;
  emit('update:modelValue', result);
  onInput(value);
  open.value = false;
  if (field.value) {
    field.value.focus();
  }
};

const textUpdate = (value: string) => {
  dateText.value = value;

  const timestamp = Date.parse(value);
  if (isNaN(timestamp)) {
    date.value = [];
    emit('update:modelValue', emptyResult.value);
    return;
  }

  const result = new Date(timestamp);
  date.value = [result];
  renderKey.value += 1;
  emit('update:modelValue', getReturnValue(result));
};

const getReturnValue = (value: Date) => {
  if (props.returnFormatted) {
    return formatText(value, typeof props.returnFormatted === 'string' ? props.returnFormatted : props.returnFormat);
  }

  if (isString.value) {
    return value.toString();
  }

  if (isNumber.value) {
    return value.getTime();
  }

  return value;
};

const formatText = (value: Date, format: string = props.format) => {
  if (!value) return '';

  try {
    return dateFormat(value, format);
  } catch {
    return '';
  }
};

const onBlur = (e: any) => {
  dateText.value = formatText(date.value[0]);
  emit('blur', e);
};

const onInput = (e: any) => {
  emit('input', e);
};

const openPicker = () => {
  open.value = true;
};

const pickerOpened = (opened: any) => {
  if (opened) {
    pickerYear.value = date.value[0] ? date.value[0].getFullYear() : new Date().getFullYear();
  }
};

const closePicker = () => {
  open.value = false;
};

const loadVariant = (year: number, month: number) => {
  const actDate = new Date(year, month, props.monthDay);

  if (date.value[0] && date.value[0].getFullYear() == actDate.getFullYear() && date.value[0].getMonth() == actDate.getMonth()) {
    return 'flat';
  }
  if (new Date().getFullYear() == actDate.getFullYear() && new Date().getMonth() == actDate.getMonth()) {
    return 'outlined';
  }

  return 'text';
};

const loadDisabled = (year: number, month: number) => {
  const actDate = new Date(year, month, props.monthDay);
  if (props.min && actDate < new Date(props.min)) {
    return true;
  }
  if (props.max && actDate > new Date(props.max)) {
    return true;
  }
  return false;
};

const selectMonth = (year: number, month: number) => {
  update([new Date(year, month, props.monthDay)]);
};

defineExpose({ openPicker, closePicker });
</script>

<template>
  <v-text-field
    :model-value="dateText"
    :append-inner-icon="appendInnerIcon"
    @update:model-value="textUpdate"
    @input="onInput"
    @change="onInput"
    @blur="onBlur">
    <v-menu
      v-model="open"
      activator="parent"
      :close-on-content-click="false"
      :disabled="$attrs.disabled as any || $attrs.readonly as any"
      @update:model-value="pickerOpened">
      <v-card
        class="pa-4 v-col-12 v-col-sm-9 v-col-md-6 bg-white"
        :key="renderKey">
        <v-card-title class="text-center">
          <v-btn
            size="small"
            class="float-left m-5"
            icon="mdi-chevron-left"
            @click="pickerYear--" />
          <span
            class="text-h2"
            style="line-height: 40px">
            {{ pickerYear }}
          </span>
          <v-btn
            size="small"
            class="float-right"
            icon="mdi-chevron-right"
            @click="pickerYear++" />
        </v-card-title>
        <transition
          name="slide-x-transition"
          mode="out-in">
          <v-card-text :key="pickerYear">
            <v-row class="mt-3">
              <v-col
                v-for="index in 12"
                :key="index"
                class="pa-1"
                :cols="4">
                <v-btn
                  class="w-100"
                  color="primary"
                  :variant="loadVariant(pickerYear, index - 1)"
                  :disabled="loadDisabled(pickerYear, index - 1)"
                  @click="selectMonth(pickerYear, index - 1)">
                  {{ $t('months.' + index) }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </transition>
      </v-card>
    </v-menu>
    <template #message="{ message }">
      <slot
        name="message"
        :message="message"></slot>
    </template>
    <template
      v-if="$slots.append"
      #append>
      <slot name="append" />
    </template>
    <template
      v-if="$slots['append-inner']"
      #append-inner>
      <slot name="append-inner" />
    </template>
    <template
      #prepend
      v-if="$slots['prepend']">
      <slot name="prepend" />
    </template>
    <template
      v-if="$slots['prepend-inner']"
      #prepend-inner>
      <slot name="prepend-inner" />
    </template>
    <template #label="{ label, props }">
      <slot
        v-if="$slots['label']"
        name="label"
        :label="label"
        :props="props" />
      <span v-else>{{ label }}</span>
    </template>
  </v-text-field>
</template>
